import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resolvePostAuthPath } from '../utils/authRedirect';
import { clearPendingAction } from '../utils/pendingAction';
import { authNotice, isServiceReason, serviceAuthPath } from '../utils/serviceApplications';

const LoginPage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState('admin');

  useEffect(() => {
    if (!isServiceReason(reason)) clearPendingAction();
  }, [reason]);

  useEffect(() => {
    if (!user) return;
    navigate(resolvePostAuthPath(searchParams), { replace: true });
  }, [user, navigate, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const dest = resolvePostAuthPath(searchParams);
      await login(email.trim(), password);
      navigate(dest, { replace: true });
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (nextRole) => {
    setRole(nextRole);
    if (nextRole === 'admin') {
      setEmail('admin@autocompare.pk');
      setPassword('admin123');
    } else {
      setEmail('user@autocompare.pk');
      setPassword('user123');
    }
  };

  return (
    <div className="auth-shell portal-autocompare">
      <div className="auth-visual">
        <div className="auth-visual-inner">
          <div className="auth-visual-badge">
            AutoCompare portal
          </div>
          <div className="auth-preview" aria-hidden="true">
            <div className="auth-preview-head">
              Upcoming test drives <span>Sample preview</span>
            </div>
            <div className="auth-preview-row first">
              <div className="auth-preview-date"><strong>28</strong><small>OCT</small></div>
              <div className="auth-preview-info"><strong>Toyota Corolla Altis</strong><span>11:00 AM, Lahore</span></div>
              <span className="auth-preview-tag">Scheduled</span>
            </div>
            <div className="auth-preview-row">
              <div className="auth-preview-date"><strong>29</strong><small>OCT</small></div>
              <div className="auth-preview-info"><strong>Honda Civic RS</strong><span>3:30 PM, Islamabad</span></div>
              <span className="auth-preview-tag pending">Pending</span>
            </div>
          </div>
          <h2>Track inquiries and test drives from one workspace.</h2>
          <p>Buyers request vehicle info and book test drives. Admins follow up from the dashboard.</p>
          <ul className="auth-feature-list">
            <li><i className="fas fa-check-circle" /> Vehicle info requests</li>
            <li><i className="fas fa-check-circle" /> Test-drive bookings</li>
            <li><i className="fas fa-check-circle" /> Admin status management</li>
          </ul>
        </div>
      </div>
      <div className="auth-panel">
        <div className="auth-card">
          <div className="brand">
            <i className="fas fa-car-side" />
            <h1>
              AutoCompare <small>PK</small>
            </h1>
          </div>
          <h2>Sign in to your account</h2>
          <p className="auth-sub">Follow up on vehicle inquiries, test drives and applications.</p>
          {isServiceReason(reason) && (
            <div className="auth-notice">
              <i className="fas fa-lock" /> {authNotice(reason)}
            </div>
          )}
          <div className="auth-role" role="group" aria-label="Sign in as">
            <button type="button" className={role === 'admin' ? 'active' : ''} aria-pressed={role === 'admin'} onClick={() => fillDemo('admin')}>
              Admin
            </button>
            <button type="button" className={role === 'user' ? 'active' : ''} aria-pressed={role === 'user'} onClick={() => fillDemo('user')}>
              User
            </button>
          </div>
          <form onSubmit={handleSubmit} className="auth-form">
            <label>
              Email address
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label>
              Password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            {error && (
              <div className="auth-error-box">
                <i className="fas fa-circle-exclamation" /> {error}
              </div>
            )}
            <button type="submit" className="btn-submit auth-submit" disabled={loading}>
              {loading ? 'Signing in…' : (
                <>
                  Sign in <i className="fas fa-arrow-right" />
                </>
              )}
            </button>
          </form>
          <p className="auth-switch">
            New here?{' '}
            <Link to={serviceAuthPath(reason, '/register')}>Create an account</Link>
          </p>
          <Link to="/" className="auth-back">
            <i className="fas fa-arrow-left" /> Back to public site
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
